// src/hooks/useTurnstile.js
//
// Cloudflare Turnstile for a public form: the landing page's contact form
// (#788) and the public issue form (#891). The widget stays hidden unless
// Cloudflare wants the visitor to tick the box, and hands back a token that
// the server verifies. One site key covers every form; `action` is what
// keeps a token solved for one form from being spent on another, so it must
// match the action the server checks for.
//
//   const captcha = useTurnstile('contact');
//   ...
//   {captcha.enabled && <div ref={captcha.containerRef} data-testid="turnstile-widget" />}
//   ...
//   if (captcha.enabled && !captcha.token) { /* say the check has not finished */ }
//   body: { ..., turnstile_token: captcha.token }
//   finally { captcha.reset(); }   // a token is single-use
//
// Without REACT_APP_TURNSTILE_SITE_KEY in the build, `enabled` is false and
// the form works without the check (local development, tests).
import { useCallback, useEffect, useRef, useState } from 'react';

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';

export function useTurnstile(action) {
  // Read at render, not import, so tests can set it; the build inlines it either way.
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
  const [token, setToken] = useState('');
  // The element is kept in state, not a ref, so the widget is drawn when the
  // element appears. The public issue form only renders once its asset has
  // loaded; with a plain ref the effect had already run by then, found no
  // element, and never drew the widget.
  const [container, setContainer] = useState(null);
  const widgetIdRef = useRef(null);

  const onVerify = useCallback((value) => setToken(value), []);

  useEffect(() => {
    if (!siteKey || !container) return undefined;
    if (!document.getElementById(TURNSTILE_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      document.head.appendChild(script);
    }

    const renderWidget = () => {
      if (widgetIdRef.current != null || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(container, {
        sitekey: siteKey,
        action,
        appearance: 'interaction-only',
        callback: onVerify,
        'expired-callback': () => setToken(''),
        'error-callback': () => setToken(''),
      });
    };

    const script = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (window.turnstile) {
      renderWidget();
    } else {
      script.addEventListener('load', renderWidget);
    }

    return () => {
      script.removeEventListener('load', renderWidget);
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
  }, [siteKey, action, container, onVerify]);

  // A token is single-use: after any attempt, the next one needs a new token.
  const reset = useCallback(() => {
    if (widgetIdRef.current != null && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
    setToken('');
  }, []);

  return { enabled: !!siteKey, token, containerRef: setContainer, reset };
}
